import { Application } from "@hotwired/stimulus";
import { navigator, session } from "@hotwired/turbo";
import * as Sentry from "@sentry/browser";
import { Integrations } from "@sentry/tracing";
import registerBehaviour from "./behaviour";
import AppearanceController from "./controller/appearance-controller";
import AvatarUploadController from "./controller/avatar-upload-controller";
import ClipboardController from "./controller/clipboard-controller";
import ClubSelectController from "./controller/club-select-controller";
import ConfettiController from "./controller/confetti-controller";
import DoublesPartnerSelectController from "./controller/doubles-partner-select-controller";
import ErrorController from "./controller/error-controller";
import LanguageSelectController from "./controller/language-select-controller";
import MatchUpEditController from "./controller/match-up-edit-controller";
import MediaViewerController from "./controller/media-viewer-controller";
import MembershipDocumentUploadController from "./controller/membership-document-upload-controller";
import NavTitleController from "./controller/nav-title-controller";
import PlatformController from "./controller/platform-controller";
import PlayerReferenceSelectController from "./controller/player-reference-select-controller";
import PlayingAbilityController from "./controller/playing-ability-controller";
import SearchController from "./controller/search-controller";
import SubmitScoreController from "./controller/submit-score-controller";

/** The response Turbo hands to `turbo:frame-render`, of which only the headers are read here. */
interface FrameRenderDetail {
  readonly fetchResponse?: { readonly response: Response };
}

const application = Application.start();

application.register("appearance", AppearanceController);
application.register("avatar-upload", AvatarUploadController);
application.register("clipboard", ClipboardController);
application.register("club-select", ClubSelectController);
application.register("confetti", ConfettiController);
application.register("doubles-partner-select", DoublesPartnerSelectController);
application.register("error", ErrorController);
application.register("language-select", LanguageSelectController);
application.register("match-up-edit", MatchUpEditController);
application.register("media-viewer", MediaViewerController);
application.register(
  "membership-document-upload",
  MembershipDocumentUploadController,
);
application.register("nav-title", NavTitleController);
application.register("platform", PlatformController);
application.register(
  "player-reference-select",
  PlayerReferenceSelectController,
);
application.register("playing-ability", PlayingAbilityController);
application.register("search", SearchController);
application.register("submit-score", SubmitScoreController);

registerBehaviour();

/* Turbo hardcodes `turbo-progress-bar` as the class on the progress bar it creates and offers no
   setting for it, so the element is tagged here and `.wp-progress-bar` is what paints it in the
   brand colour. The bar is built by the session's adapter at import time and is the same element
   for the life of the page, so tagging it once at boot covers every navigation. */
/* `progressBar` is on Turbo's BrowserAdapter rather than on the `Adapter` interface the session
   is typed with, so it is reached through a narrow local shape. Every hop stays optional: on a
   session using a different adapter there is no bar, and that is not an error. */
const adapter = session.adapter as unknown as
  { progressBar?: { progressElement?: HTMLElement } } | undefined;
adapter?.progressBar?.progressElement?.classList.add("wp-progress-bar");

/* Reporting is off in development, where a stack trace in the console is more use than one in
   Sentry and the noise would otherwise be attributed to the production project. */
const profile = document.querySelector<HTMLMetaElement>(
  'meta[name="ApplicationProfile"]',
);
if (profile?.content !== "dev") {
  Sentry.init({
    dsn: "https://8b2446049dcd42e09078a4c847654660@o226940.ingest.sentry.io/6178573",
    integrations: [new Integrations.BrowserTracing()],

    /* 1.0 captures every transaction for performance monitoring; worth revisiting if the
           volume ever becomes a cost. */
    tracesSampleRate: 1.0,
  });
}

/*
 * A frame that answers with `X-Navigator-History-Url` is saying "this frame swap was a navigation,
 * put it in the address bar": Turbo does not change the URL for a frame render on its own.
 */
document.addEventListener("turbo:frame-render", (event) => {
  const detail = (event as CustomEvent<FrameRenderDetail>).detail;
  const url = detail.fetchResponse?.response.headers.get(
    "X-Navigator-History-Url",
  );
  if (url === null || url === undefined || url === "") {
    return;
  }

  const current = new URL(window.location.href);

  navigator.history.push(new URL(`${current.origin}${url}`));
});

/* The server formats every date and time in the viewer's own zone, and this is the only way the
   browser can tell it which that is. */
document.cookie = `TIMEZONE=${Intl.DateTimeFormat().resolvedOptions().timeZone}; path=/; SameSite=Strict; Secure`;
